/**
 * The Squares half of the create flow's Review step (ISSUE-1269). Before the
 * Commissioner taps Create pool, it restates the setup: the grid type, the
 * games and how many grids that makes, the axes, the winning squares and the
 * grid settings. Every label comes from the league's presets
 * (`/squares/presets-get`, already cached by the setup step) and the count
 * from the server's preview the draft kept. One game also names the matchup,
 * read from the same server preview the picker showed (QA round 3). Nothing
 * is decided here.
 */
import {
  useSquaresPresetsGet,
  useSquaresPreviewGames,
} from "@sportsmet/contest-sdk/private";
import type { ReactNode } from "react";

import {
  SQUARES_GRID_SIZE_OPTION,
  SQUARES_MAX_PER_MEMBER_UNLIMITED,
  SQUARES_REVIEW_AXES,
  SQUARES_REVIEW_AUTO_LOCK,
  SQUARES_REVIEW_CHECKPOINTS,
  SQUARES_REVIEW_GAMES,
  SQUARES_REVIEW_GRID_TYPE,
  SQUARES_REVIEW_MAX_PER_MEMBER,
  SQUARES_REVIEW_OFF,
  SQUARES_REVIEW_ON,
  SQUARES_REVIEW_SHOW_NUMBERS,
  SQUARES_REVIEW_SIZE,
  SQUARES_REVIEW_TITLE,
  squaresCreateGridCountLine,
  squaresGridTypeName,
} from "./squaresCopy";
import type { typeSquaresSetupDraft } from "./squaresCreateSetupDraft";
import { squaresMatchupLabel } from "./squaresModel";
import type { typeSquaresPresetsData } from "./squaresTypes";
import { useSquaresLeagueTeams } from "./useSquaresLeagueTeams";

const PRESETS_STALE_TIME_MS = 10 * 60 * 1000;

export type typeSquaresReviewRow = { label: string; value: string };

/** The review rows for one draft, or [] until the grid type is chosen. Pure. */
export function squaresReviewRows(input: {
  draft: typeSquaresSetupDraft;
  presets: typeSquaresPresetsData | null;
  /** One game's "Away at Home", once the server preview has answered. */
  oneGameMatchup?: string | null | undefined;
}): readonly typeSquaresReviewRow[] {
  const { draft, presets } = input;
  if (draft.gridType === null) return [];
  const rows: typeSquaresReviewRow[] = [
    {
      label: SQUARES_REVIEW_GRID_TYPE,
      value: squaresGridTypeName(draft.gridType),
    },
  ];
  const rule = draft.gameRule;
  if (rule !== null) {
    const presetLabel =
      presets?.gamePresets.find((preset) => preset.presetKey === rule.presetKey)
        ?.label ?? null;
    const count =
      draft.preview === null || draft.preview === undefined
        ? null
        : squaresCreateGridCountLine({
            gridType: draft.gridType,
            includedCount: draft.preview.includedCount,
          });
    const matchup =
      rule.presetKey === "oneGame" ? (input.oneGameMatchup ?? null) : null;
    const value = [presetLabel, matchup, count]
      .filter((part): part is string => part !== null && part !== "")
      .join(" · ");
    if (value !== "") rows.push({ label: SQUARES_REVIEW_GAMES, value });
  }
  const axes = draft.axes;
  if (axes !== null) {
    const value =
      axes.kind === "custom"
        ? `${(axes.topLabel ?? "").trim()} / ${(axes.sideLabel ?? "").trim()}`
        : (presets?.axesPresets.find((preset) => preset.kind === axes.kind)
            ?.label ?? null);
    if (value !== null) rows.push({ label: SQUARES_REVIEW_AXES, value });
  }
  const checkpointLabels = (presets?.checkpoints ?? [])
    .filter((checkpoint) =>
      draft.checkpoints.includes(checkpoint.checkpointKey),
    )
    .map((checkpoint) => checkpoint.label);
  if (checkpointLabels.length > 0) {
    rows.push({
      label: SQUARES_REVIEW_CHECKPOINTS,
      value: checkpointLabels.join(" · "),
    });
  }
  const settings = draft.gridSettings;
  if (settings !== null) {
    rows.push(
      {
        label: SQUARES_REVIEW_SIZE,
        value: SQUARES_GRID_SIZE_OPTION(settings.size),
      },
      {
        label: SQUARES_REVIEW_MAX_PER_MEMBER,
        value:
          settings.maxPerMember === null || settings.maxPerMember === undefined
            ? SQUARES_MAX_PER_MEMBER_UNLIMITED
            : String(settings.maxPerMember),
      },
      {
        label: SQUARES_REVIEW_AUTO_LOCK,
        value: settings.autoLock ? SQUARES_REVIEW_ON : SQUARES_REVIEW_OFF,
      },
      {
        label: SQUARES_REVIEW_SHOW_NUMBERS,
        value: settings.showNumbers ? SQUARES_REVIEW_ON : SQUARES_REVIEW_OFF,
      },
    );
  }
  return rows;
}

export const SquaresCreateReviewSummary = (props: {
  leagueKey: string | null;
  draft: typeSquaresSetupDraft;
}): ReactNode => {
  const presetsQuery = useSquaresPresetsGet(
    { leagueKey: props.leagueKey ?? "" },
    { enabled: props.leagueKey !== null, staleTime: PRESETS_STALE_TIME_MS },
  );
  const rule = props.draft.gameRule;
  const oneGameEventId =
    rule?.presetKey === "oneGame" ? (rule.eventId ?? null) : null;
  // The same request the setup step's preview made, so it is usually cached.
  const oneGamePreview = useSquaresPreviewGames(
    {
      leagueKey: props.leagueKey ?? "",
      gridType: props.draft.gridType ?? "classic",
      gameRule: rule ?? { presetKey: "allGames", excludedEventIds: [] },
    },
    {
      enabled:
        props.leagueKey !== null &&
        props.draft.gridType !== null &&
        oneGameEventId !== null,
    },
  );
  const teams = useSquaresLeagueTeams(props.leagueKey);
  const oneGame =
    oneGameEventId === null
      ? undefined
      : oneGamePreview.data?.data?.games.find(
          (game) => game.eventId === oneGameEventId,
        );
  const rows = squaresReviewRows({
    draft: props.draft,
    presets: presetsQuery.data?.data ?? null,
    oneGameMatchup:
      oneGame === undefined || teams.isLoading
        ? null
        : squaresMatchupLabel({
            awayTeamId: oneGame.awayTeamId,
            homeTeamId: oneGame.homeTeamId,
            teamsById: teams.teamsById,
          }),
  });
  if (rows.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-3"
      aria-label={SQUARES_REVIEW_TITLE}
      data-testid="squares-create-review"
    >
      <h3 className="text-body-strong text-ink">{SQUARES_REVIEW_TITLE}</h3>
      <dl className="flex flex-col gap-3">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-caption text-ink-secondary">{row.label}</dt>
            <dd className="text-body-strong text-ink">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
